import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { isMobile } from "react-device-detect";
import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import { useSwipeable } from "react-swipeable";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import axios from "../../../axios";

import { Stack } from "@mui/system";
import { BookLoader } from "../../../components/common/BookLoader";
import { ProfileCard } from "./ProfileCard.js";
import { ParticipantsList } from "./ParticipantsList.js";
import { useTrip } from "../../../services/trip";
import { useProject } from "../../../services/project";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { ImageResizable } from "../../../components/elements/ImageResizeable";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useIsAuthenticated } from "../../../utils/auth";

import "./GroupDetails.scss";
import { useTranslation } from "react-i18next";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export const GroupDetails = ({ tripId, open, handleClose, friend_id = null }) => {
  const { t } = useTranslation();
  const { data: trip, isLoading: loadingTrip } = useTrip(tripId, true);
  // The single-trip payload (getTrip) does not carry projects.form_topic, so fetch
  // the project to drive the SoClub waitlist form-gate on the "Group chat" button.
  // SWR dedupes with the project page's own fetch; no-ops until trip loads.
  const { data: project } = useProject(trip?.project_id);
  const { isAuthenticated } = useIsAuthenticated();

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      if (eventData.dir === "Right") {
        handleClose();
      }
    },
  });

  const [tripStats, setTripStats] = useState(null);
  const [loadingTripStats, setLoadingTripStats] = useState(true);
  const [loadingFriend, setLoadingFriend] = useState(true);
  const [tripParticipants, setTripParticipants] = useState([]);

  const [view, setView] = useState("summary"); // participants, summary

  useEffect(() => {
    const getTripStats = async () => {
      setLoadingTripStats(true);
      try {
        const res = await axios.get("/v1/trip/getTripStatistics", {
          params: { tripId: trip.trip_id },
        });

        setTripStats(res.data);
        setLoadingTripStats(false);
      } catch (err) {
        setLoadingTripStats(false);
        console.log("failed to get trip stats", err);
      }
    };

    const getTripParticipantsWeb = async () => {
      try {
        const res = await axios.get("/v1/trip/getTripParticipantsWeb", {
          params: { tripId: trip.trip_id },
        });
        setTripParticipants(res.data);
      } catch (err) {
        console.log("failed to get trip participants", err);
      }
    };

    if (trip) {
      getTripStats();
      getTripParticipantsWeb();
    }
  }, [trip]);

  const GroupDetailsChild = () => {
    if (loadingTrip || loadingTripStats) {
      return (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <BookLoader />
          <span style={{ fontSize: "12px", marginTop: "10px", fontWeight: "500" }}>
            {t("loading_group_details", {
              defaultValue: "loading group details ..",
            })}{" "}
            ..
          </span>
        </Stack>
      );
    }

    if (view === "summary") {
      return (
        <div
          className="TripDetailsGroupWrapper"
          style={isMobile ? { width: "88%", marginLeft: "6%" } : {}}
        >
          <ProfileCard
            trip={trip}
            tripStats={tripStats}
            handlers={handlers}
            setView={setView}
            friend_id={friend_id}
            form_topic={project?.form_topic}
          />
        </div>
      );
    } else if (view === "participants") {
      return (
        <div style={isMobile ? { marginTop: "4rem" } : {}}>
          <ParticipantsList participants={tripParticipants} tripId={tripId} trip={trip} />
        </div>
      );
    }
  };

  // Disable scrolling when viewing participants and not authenticated
  const shouldDisableScroll = view === "participants" && !isAuthenticated;

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        TransitionComponent={Transition}
        fullScreen
        PaperProps={{
          style: {
            overflow: shouldDisableScroll ? "hidden" : "auto",
          },
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            display: "flex",
            position: "fixed",
            top: 0,
            width: "100%",
            height: "3.5rem",
            backgroundColor: "white",
            opacity: 0.99,
            zIndex: 100,
          }}
          {...handlers}
        >
          {view == "participants" ? (
            <button style={{ height: "4rem", width: "4rem" }} onClick={() => setView("summary")}>
              <ArrowBackIosIcon
                style={{
                  fontSize: "24px",
                }}
              />
            </button>
          ) : (
            <button style={{ height: "4rem", width: "4rem" }} onClick={handleClose}>
              <CloseIcon
                style={{
                  fontSize: "28px",
                }}
              />
            </button>
          )}
        </div>
        {GroupDetailsChild()}
      </Dialog>
    );
  } else {
    const modalStyle = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      height: 650,
      width: 780,
      bgcolor: "background.paper",
      borderRadius: 5,
      p: 4,
      overflowY: shouldDisableScroll ? "hidden" : "scroll",
    };

    return (
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        TransitionComponent={Transition}
      >
        <Box sx={modalStyle}>{GroupDetailsChild()}</Box>
      </Modal>
    );
  }
};
